import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ProjectStatusIndicator from '../../../components/ui/ProjectStatusIndicator';

const ProjectCard = ({ project }) => {
  const navigate = useNavigate();

  // Calculate progress based on phase
  const getProgressFromPhase = (phase) => {
    const phaseProgress = {
      planning: 10,
      permits: 25,
      foundation: 40,
      framing: 55,
      systems: 70,
      finishing: 85,
      completed: 100
    };
    return phaseProgress?.[phase] || 0;
  };

  const progress = getProgressFromPhase(project?.phase);

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'bg-success';
    if (progress >= 50) return 'bg-accent';
    if (progress >= 25) return 'bg-warning';
    return 'bg-error';
  };

  // Format due date
  const formatDueDate = (dueDate) => {
    if (!dueDate) return 'Not set';
    try {
      const date = new Date(dueDate);
      return date?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  // Format budget
  const formatBudget = (budget) => {
    if (!budget) return 'N/A';
    return `$${(budget / 1000)?.toFixed(0)}K`;
  };

  // Format ADU type for display
  const formatAduType = (aduType) => {
    if (!aduType) return 'N/A';
    return aduType?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  const handleClick = () => {
    if (project?.id) {
      // Dispatch event to open project details
      const event = new CustomEvent('openProjectDetails', {
        detail: { projectId: project?.id }
      });
      window.dispatchEvent(event);
      // Navigate to projects page where the detail panel will open
      navigate('/projects');
    }
  };

  return (
    <div 
      className="bg-card border border-border rounded-xl overflow-hidden smooth-transition hover:shadow-lg hover:-translate-y-1 cursor-pointer min-h-[44px]"
      onClick={handleClick}
    >
      <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-foreground line-clamp-1 mb-1">
              {project?.customerName || 'Unnamed Project'}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-1">
              {project?.address || 'No address'}
            </p>
          </div>
          <ProjectStatusIndicator status={project?.phase} />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Icon name="Home" size={16} />
            <span>{formatAduType(project?.aduType)}</span>
          </div>
          <span className="hidden sm:inline text-border">•</span>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Icon name="MapPin" size={16} />
            <span className="line-clamp-1">{project?.address?.split(',')?.[0] || 'No location'}</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold text-foreground">{progress}%</span>
          </div>
          <div className="w-full h-2.5 sm:h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full ${getProgressColor(progress)} smooth-transition`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Icon name="Calendar" size={16} />
            <span className="text-xs sm:text-sm">Due: {formatDueDate(project?.dueDate)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Icon name="DollarSign" size={16} />
            <span className="text-xs sm:text-sm">{formatBudget(project?.budgetTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;